import { useRef, useCallback, useEffect, useState } from "react";
import { getOrderBookSseEndpoint } from "@/utils/api";

export interface OrderBookSnapshotProps {
  timestamp: number;
  exchange: string;
  pairSymbol: string;
  bids: [number, number][];
  asks: [number, number][];
}

interface UseOrderBookSSEProps {
  pairSymbol?: string;
  exchangeId?: string;
}

// if we don't receive data for 10 seconds, we consider the pair to be unavailable
const PAIR_UNAVAILABILITY_TIMEOUT = 10_000;
const THROTTLE_DELAY = 100; // 100ms throttle delay

export const useOrderBookSSE = ({ pairSymbol, exchangeId }: UseOrderBookSSEProps) => {
  const activeEventSource = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const pairAvailabilityTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const throttleTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const lastUpdateTimeRef = useRef<number>(0);
  const isConnecting = useRef(false);
  const [isConnected, setIsConnected] = useState(false);
  const [orderBookData, setOrderBookData] = useState<OrderBookSnapshotProps | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPairUnavailable, setIsPairUnavailable] = useState(false);

  const updateOrderBookData = useCallback((data: OrderBookSnapshotProps) => {
    setOrderBookData(data);
    setError(null);
    setIsPairUnavailable(false);

    // Clear the pair availability timeout since we received data
    if (pairAvailabilityTimeoutRef.current) {
      clearTimeout(pairAvailabilityTimeoutRef.current);
      pairAvailabilityTimeoutRef.current = undefined;
    }
  }, []);

  const handleOrderBookUpdate = useCallback(
    (data: OrderBookSnapshotProps) => {
      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

      if (timeSinceLastUpdate >= THROTTLE_DELAY) {
        // Enough time has passed since last update (100ms+)
        // Show this update immediately without delay
        lastUpdateTimeRef.current = now;
        updateOrderBookData(data);
      } else {
        // Too soon since last update (< 100ms)
        // Cancel any pending delayed update (we want the latest data)
        if (throttleTimeoutRef.current) {
          clearTimeout(throttleTimeoutRef.current);
        }

        // Schedule this update to show after the remaining throttle time
        // This ensures we don't lose the latest data, just delay it
        const remainingTime = THROTTLE_DELAY - timeSinceLastUpdate;
        throttleTimeoutRef.current = setTimeout(() => {
          lastUpdateTimeRef.current = Date.now();
          updateOrderBookData(data);
        }, remainingTime);
      }
    },
    [updateOrderBookData],
  );

  const disconnectSse = useCallback(() => {
    if (activeEventSource.current) {
      activeEventSource.current.close();
      activeEventSource.current = null;
    }

    // Clear any pending reconnect attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }

    // Clear pair availability timeout
    if (pairAvailabilityTimeoutRef.current) {
      clearTimeout(pairAvailabilityTimeoutRef.current);
      pairAvailabilityTimeoutRef.current = undefined;
    }

    // Clear throttle timeout
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
      throttleTimeoutRef.current = undefined;
    }

    // Reset connection state
    isConnecting.current = false;
    setIsConnected(false);
    // Reset last update time
    lastUpdateTimeRef.current = 0;
  }, []);

  const connectSse = useCallback(async () => {
    // Only connect if not already connecting and have valid params
    if (isConnecting.current || !pairSymbol || !exchangeId) return;

    // Disconnect any existing connection first
    disconnectSse();

    isConnecting.current = true;
    setError(null);
    setIsPairUnavailable(false);
    // Reset data when starting new connection
    setOrderBookData(null);

    try {
      const endpoint = getOrderBookSseEndpoint(pairSymbol, exchangeId);
      const eventSource = new EventSource(endpoint);

      eventSource.addEventListener("open", () => {
        setIsConnected(true);
        isConnecting.current = false;
        // Clear any pending reconnect attempts
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      });

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      eventSource.addEventListener("connection-established", (_event) => {
        try {
          // Start timer to check if we receive orderbook data within 10 seconds
          pairAvailabilityTimeoutRef.current = setTimeout(() => {
            setIsPairUnavailable(true);
          }, PAIR_UNAVAILABILITY_TIMEOUT);
        } catch {
          // Connection established parsing failed - not critical
        }
      });

      eventSource.addEventListener("orderbook-update", (event) => {
        try {
          const parsedData = JSON.parse(event.data);

          if (parsedData.timestamp && parsedData.bids && parsedData.asks) {
            handleOrderBookUpdate(parsedData as OrderBookSnapshotProps);
          }
        } catch {
          setError("Failed to parse orderbook update");
        }
      });

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      eventSource.addEventListener("error", () => {
        isConnecting.current = false;
        setIsConnected(false);
        setError("Connection error");

        // Close the current connection
        if (activeEventSource.current) {
          activeEventSource.current.close();
          activeEventSource.current = null;
        }

        // Attempt to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connectSse();
        }, 5000);
      });

      activeEventSource.current = eventSource;
    } catch {
      isConnecting.current = false;
      setError("Failed to create connection");
    }
  }, [pairSymbol, exchangeId, handleOrderBookUpdate, disconnectSse]);

  // Handle connection and visibility changes
  useEffect(() => {
    if (!pairSymbol || !exchangeId) {
      disconnectSse();
      return;
    }

    // Connect initially
    connectSse();

    // Handle visibility changes
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        disconnectSse();
      } else {
        connectSse();
      }
    };

    window.addEventListener("visibilitychange", onVisibilityChange);

    // Cleanup
    return () => {
      window.removeEventListener("visibilitychange", onVisibilityChange);
      disconnectSse();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pairAvailabilityTimeoutRef.current) {
        clearTimeout(pairAvailabilityTimeoutRef.current);
      }
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, [pairSymbol, exchangeId, connectSse, disconnectSse]);

  return {
    isConnected,
    orderBookData: orderBookData ?? undefined,
    error,
    isPairUnavailable,
    disconnect: disconnectSse,
    reconnect: connectSse,
  };
};
